
import { useState } from 'react';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Define the shape of the form data for stronger type‑checking */
interface FormData {
  nom_cabinet: string;
  nom_contact: string;
  email: string;
  telephone: string;
  creneau_prefere: string;
  jour_prefere: string;
  problematique: string;
  objectifs: string;
  message: string;
}

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const [formData, setFormData] = useState<FormData>({
    nom_cabinet: '',
    nom_contact: '',
    email: '',
    telephone: '',
    creneau_prefere: '',
    jour_prefere: '',
    problematique: '',
    objectifs: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const creneaux = [
    '9h00 - 10h00',
    '10h00 - 11h00',
    '11h00 - 12h00',
    '14h00 - 15h00',
    '15h00 - 16h00',
    '16h00 - 17h00',
    '17h00 - 18h00',
  ];

  const jours = [
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ];

  const problematiques = [
    'Surcharge de travail',
    'Manque de rentabilité',
    'Difficultés de recrutement',
    'Gestion des délais',
    'Qualité du service client',
    'Développement commercial',
    'Organisation interne',
    'Autre',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('nom_cabinet', formData.nom_cabinet);
      formDataToSubmit.append('nom_contact', formData.nom_contact);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('telephone', formData.telephone);
      formDataToSubmit.append('creneau_prefere', formData.creneau_prefere);
      formDataToSubmit.append('jour_prefere', formData.jour_prefere);
      formDataToSubmit.append('problematique', formData.problematique);
      formDataToSubmit.append('objectifs', formData.objectifs);
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d304227mrrhjc90busqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit,
      });

      if (response.ok) {
        setSubmitMessage(
          "Votre demande d'entretien a été envoyée avec succès ! Nous vous recontacterons sous 24h pour confirmer le rendez-vous."
        );
        // Reset the form fields after a successful submission
        setFormData({
          nom_cabinet: '',
          nom_contact: '',
          email: '',
          telephone: '',
          creneau_prefere: '',
          jour_prefere: '',
          problematique: '',
          objectifs: '',
          message: '',
        });
      } else {
        setSubmitMessage("Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitMessage("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Planifier un entretien gratuit</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {submitMessage && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                submitMessage.includes('succès') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {submitMessage}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="w-6 h-6 flex items-center justify-center mr-3">
                <i className="ri-information-line text-blue-600"></i>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800 mb-2">Votre consultation gratuite inclut :</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Audit de votre situation actuelle (30 min)</li>
                  <li>• Analyse de vos besoins spécifiques</li>
                  <li>• Présentation de nos solutions personnalisées</li>
                  <li>• Estimation tarifaire détaillée</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form id="consultation-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom du cabinet *</label>
                <input
                  type="text"
                  name="nom_cabinet"
                  value={formData.nom_cabinet}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nom_cabinet: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom du contact *</label>
                <input
                  type="text"
                  name="nom_contact"
                  value={formData.nom_contact}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nom_contact: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, telephone: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Jour préféré *</label>
                <select
                  name="jour_prefere"
                  value={formData.jour_prefere}
                  onChange={(e) => setFormData((prev) => ({ ...prev, jour_prefere: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez un jour</option>
                  {jours.map((jour) => (
                    <option key={jour} value={jour}>
                      {jour}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Créneau préféré *</label>
                <select
                  name="creneau_prefere"
                  value={formData.creneau_prefere}
                  onChange={(e) => setFormData((prev) => ({ ...prev, creneau_prefere: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  required
                >
                  <option value="">Sélectionnez un créneau</option>
                  {creneaux.map((creneau) => (
                    <option key={creneau} value={creneau}>
                      {creneau}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Principale problématique *
              </label>
              <select
                name="problematique"
                value={formData.problematique}
                onChange={(e) => setFormData((prev) => ({ ...prev, problematique: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                required
              >
                <option value="">Sélectionnez votre problématique</option>
                {problematiques.map((problematique) => (
                  <option key={problematique} value={problematique}>
                    {problematique}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vos objectifs avec l'externalisation
              </label>
              <textarea
                name="objectifs"
                value={formData.objectifs}
                onChange={(e) => setFormData((prev) => ({ ...prev, objectifs: e.target.value }))}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Ex: Réduire les coûts, améliorer la qualité, gagner du temps..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.objectifs.length}/500 caractères</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Message complémentaire</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Informations complémentaires..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caractères</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Planifier mon entretien'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
